<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-rose-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">파트너스 정사각형 배너</h1>
            <p class="text-sm text-slate-500">파트너스 대시보드 사이드에 표시되는 정사각형 배너를 관리합니다</p>
          </div>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          배너 등록
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-rose-600/30 border-t-rose-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">로딩 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="banners.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">등록된 배너가 없습니다</h3>
        <p class="text-slate-400 mb-4">파트너스 대시보드 사이드에 표시할 정사각형 배너를 등록해보세요.</p>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          배너 등록
        </button>
      </div>

      <!-- 배너 목록 (그리드) -->
      <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div
          v-for="(banner, index) in banners"
          :key="banner.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <!-- 배너 이미지 (정사각형) -->
          <div class="aspect-square bg-slate-100 relative">
            <img
              :src="banner.imageUrl"
              alt="배너 이미지"
              class="w-full h-full object-cover"
            />
            <div class="absolute top-2 left-2 flex items-center gap-1">
              <span class="px-2 py-0.5 text-[10px] font-bold bg-black/50 text-white rounded-full">
                {{ index + 1 }}
              </span>
              <span :class="[
                'px-2 py-0.5 text-[10px] font-bold rounded-full',
                banner.isActive ? 'bg-green-500 text-white' : 'bg-slate-500 text-white'
              ]">
                {{ banner.isActive ? 'ON' : 'OFF' }}
              </span>
            </div>
          </div>

          <!-- 배너 정보 -->
          <div class="p-3">
            <p class="text-xs text-slate-400 truncate mb-2">
              {{ banner.linkUrl || '링크 없음' }}
            </p>

            <!-- 액션 버튼 -->
            <div class="flex items-center gap-1">
              <button
                @click="toggleActive(banner)"
                :disabled="processing === banner.id"
                :class="[
                  'flex-1 px-2 py-1.5 text-xs rounded-lg transition',
                  banner.isActive
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-green-600 hover:bg-green-50'
                ]"
              >
                {{ banner.isActive ? 'OFF' : 'ON' }}
              </button>
              <button
                @click="openEditModal(banner)"
                class="flex-1 px-2 py-1.5 text-xs text-rose-600 hover:bg-rose-50 rounded-lg transition"
              >
                수정
              </button>
              <button
                @click="deleteBanner(banner)"
                :disabled="processing === banner.id"
                class="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                삭제
              </button>
            </div>

            <!-- 순서 버튼 -->
            <div class="flex items-center gap-1 mt-1">
              <button
                @click="moveUp(index)"
                :disabled="index === 0"
                class="flex-1 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30"
              >
                ← 앞으로
              </button>
              <button
                @click="moveDown(index)"
                :disabled="index === banners.length - 1"
                class="flex-1 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30"
              >
                뒤로 →
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 배너 등록/수정 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">
                {{ editingBanner ? '배너 수정' : '배너 등록' }}
              </h3>

              <form @submit.prevent="saveBanner" class="space-y-4">
                <!-- 이미지 업로드 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">배너 이미지 *</label>
                  <div
                    @click="$refs.fileInput.click()"
                    @dragover.prevent
                    @drop.prevent="handleDrop"
                    class="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-rose-500 transition"
                  >
                    <div v-if="previewUrl" class="relative">
                      <img :src="previewUrl" class="w-48 h-48 object-cover rounded-lg mx-auto"/>
                      <button
                        type="button"
                        @click.stop="clearImage"
                        class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                    <div v-else class="py-6">
                      <svg class="w-12 h-12 mx-auto text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z"/>
                      </svg>
                      <p class="text-sm text-slate-500">클릭하거나 이미지를 드래그하세요</p>
                      <p class="text-xs text-slate-400 mt-1">권장 비율 1:1 (정사각형, 예: 400x400)</p>
                    </div>
                  </div>
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileSelect"
                  />
                </div>

                <!-- 링크 URL -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">클릭 시 이동 URL</label>
                  <input
                    v-model="form.linkUrl"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    placeholder="https://example.com 또는 /partners/..."
                  />
                  <p class="text-xs text-slate-400 mt-1">외부 링크(http://) 또는 내부 경로(/partners/...) 입력 가능</p>
                </div>

                <!-- 활성화 여부 -->
                <div class="flex items-center gap-3">
                  <input
                    v-model="form.isActive"
                    type="checkbox"
                    id="isActive"
                    class="w-5 h-5 text-rose-600 rounded focus:ring-rose-500"
                  />
                  <label for="isActive" class="text-sm text-slate-700">배너 활성화 (파트너스 대시보드에 노출)</label>
                </div>

                <!-- 버튼 -->
                <div class="flex gap-2 pt-4">
                  <button
                    type="button"
                    @click="closeModal"
                    class="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    :disabled="!canSubmit || submitting"
                    class="flex-1 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition disabled:opacity-50"
                  >
                    {{ submitting ? '저장 중...' : (editingBanner ? '수정하기' : '등록하기') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, writeBatch } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'PartnerSquareBannerManageView',
  components: {
    AdminLayout
  },
  setup() {
    const loading = ref(true)
    const banners = ref([])
    const processing = ref(null)
    const showModal = ref(false)
    const editingBanner = ref(null)
    const submitting = ref(false)

    const form = ref({
      linkUrl: '',
      isActive: true
    })

    const selectedFile = ref(null)
    const previewUrl = ref('')

    const canSubmit = computed(() => {
      return previewUrl.value || editingBanner.value?.imageUrl
    })

    const loadBanners = async () => {
      try {
        loading.value = true
        const q = query(
          collection(db, 'partnerSquareBanners'),
          orderBy('order', 'asc')
        )
        const snapshot = await getDocs(q)
        banners.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('배너 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const openCreateModal = () => {
      editingBanner.value = null
      form.value = {
        linkUrl: '',
        isActive: true
      }
      selectedFile.value = null
      previewUrl.value = ''
      showModal.value = true
    }

    const openEditModal = (banner) => {
      editingBanner.value = banner
      form.value = {
        linkUrl: banner.linkUrl || '',
        isActive: banner.isActive !== false
      }
      selectedFile.value = null
      previewUrl.value = banner.imageUrl || ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingBanner.value = null
      form.value = {
        linkUrl: '',
        isActive: true
      }
      selectedFile.value = null
      previewUrl.value = ''
    }

    const handleFileSelect = (e) => {
      const file = e.target.files[0]
      if (file) {
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
      }
    }

    const handleDrop = (e) => {
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
      }
    }

    const clearImage = () => {
      selectedFile.value = null
      previewUrl.value = editingBanner.value?.imageUrl || ''
    }

    const uploadImage = async (file) => {
      const fileName = `partner-square-banners/${Date.now()}_${file.name}`
      const fileRef = storageRef(storage, fileName)
      await uploadBytes(fileRef, file)
      return getDownloadURL(fileRef)
    }

    const saveBanner = async () => {
      if (!canSubmit.value) return

      submitting.value = true
      try {
        let imageUrl = editingBanner.value?.imageUrl || ''

        if (selectedFile.value) {
          imageUrl = await uploadImage(selectedFile.value)
        }

        const bannerData = {
          linkUrl: form.value.linkUrl.trim(),
          imageUrl,
          isActive: form.value.isActive,
          updatedAt: new Date().toISOString()
        }

        if (editingBanner.value) {
          await updateDoc(doc(db, 'partnerSquareBanners', editingBanner.value.id), bannerData)
          alert('배너가 수정되었습니다.')
        } else {
          bannerData.order = banners.value.length
          bannerData.createdAt = new Date().toISOString()
          await addDoc(collection(db, 'partnerSquareBanners'), bannerData)
          alert('배너가 등록되었습니다.')
        }

        closeModal()
        await loadBanners()
      } catch (error) {
        console.error('배너 저장 실패:', error)
        alert('저장에 실패했습니다: ' + error.message)
      } finally {
        submitting.value = false
      }
    }

    const toggleActive = async (banner) => {
      processing.value = banner.id
      try {
        await updateDoc(doc(db, 'partnerSquareBanners', banner.id), {
          isActive: !banner.isActive,
          updatedAt: new Date().toISOString()
        })
        await loadBanners()
      } catch (error) {
        console.error('상태 변경 실패:', error)
        alert('상태 변경에 실패했습니다.')
      } finally {
        processing.value = null
      }
    }

    const deleteBanner = async (banner) => {
      if (!confirm('이 배너를 삭제하시겠습니까?')) return

      processing.value = banner.id
      try {
        await deleteDoc(doc(db, 'partnerSquareBanners', banner.id))
        await loadBanners()
        alert('배너가 삭제되었습니다.')
      } catch (error) {
        console.error('삭제 실패:', error)
        alert('삭제에 실패했습니다.')
      } finally {
        processing.value = null
      }
    }

    const updateOrder = async () => {
      try {
        const batch = writeBatch(db)
        banners.value.forEach((banner, index) => {
          const bannerRef = doc(db, 'partnerSquareBanners', banner.id)
          batch.update(bannerRef, { order: index })
        })
        await batch.commit()
      } catch (error) {
        console.error('순서 업데이트 실패:', error)
      }
    }

    const moveUp = async (index) => {
      if (index === 0) return
      const temp = banners.value[index]
      banners.value[index] = banners.value[index - 1]
      banners.value[index - 1] = temp
      await updateOrder()
    }

    const moveDown = async (index) => {
      if (index === banners.value.length - 1) return
      const temp = banners.value[index]
      banners.value[index] = banners.value[index + 1]
      banners.value[index + 1] = temp
      await updateOrder()
    }

    onMounted(loadBanners)

    return {
      loading,
      banners,
      processing,
      showModal,
      editingBanner,
      submitting,
      form,
      previewUrl,
      canSubmit,
      openCreateModal,
      openEditModal,
      closeModal,
      handleFileSelect,
      handleDrop,
      clearImage,
      saveBanner,
      toggleActive,
      deleteBanner,
      moveUp,
      moveDown
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
